import { useEffect, useState } from "react";
import { Ruler, Trash2, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  resolveMeasurementProvider,
  PROVIDER_GOOGLE,
} from "@/lib/measurementProvider";
import MeasurementWorkspace from "@/pages/MeasurementWorkspace";

export default function MeasurementsPanel({
  selectedProp, measurements, setMeasurements, onAddLine, onNewMeasurement,
  propertyAddress, propertyLat, propertyLng,
}) {
  const [showWorkspace, setShowWorkspace] = useState(false);
  const [googleAvailable, setGoogleAvailable] = useState(false);

  useEffect(() => {
    // Ask the backend once whether Google is enabled. If yes AND the browser
    // key is present, expose the "Draw on map" affordance. If not, the
    // existing Mapbox flow stays untouched.
    let mounted = true;
    resolveMeasurementProvider().then((d) => {
      if (mounted) setGoogleAvailable(d.provider === PROVIDER_GOOGLE);
    });
    return () => { mounted = false; };
  }, []);
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 card-crisp">
      <div className="flex items-center gap-2 mb-3">
        <Ruler className="w-4 h-4 text-[#FF5A00]" />
        <div className="font-heading font-bold">Measurements</div>
        <span className="ml-auto text-xs text-zinc-500 font-mono-accent">{measurements.length}</span>
        {googleAvailable && (
          <button
            disabled={!selectedProp}
            onClick={() => setShowWorkspace(true)}
            className="text-xs bg-[#FF5A00] text-white px-2 py-1 rounded disabled:opacity-40"
            data-testid="draw-on-map-btn"
          >
            <MapPin className="w-3 h-3 inline mr-1" /> Draw on map
          </button>
        )}
        <button
          disabled={!selectedProp}
          onClick={onNewMeasurement}
          className="text-xs bg-zinc-900 text-white px-2 py-1 rounded disabled:opacity-40"
          data-testid="new-measurement-btn"
        >
          + Add
        </button>
      </div>
      <MeasurementWorkspace
        open={showWorkspace}
        onClose={() => setShowWorkspace(false)}
        propertyAddress={propertyAddress}
        propertyLat={propertyLat}
        propertyLng={propertyLng}
        onSaved={(m) => {
          // Normalize the /api/measurements response into the shape the panel
          // uses (label / output_value / output_unit) so the existing list UI
          // shows it without a full estimator refactor.
          const compat = {
            id: m.id,
            label: m.service_category
              ? `${m.service_category} (${m.measurement_type})`
              : m.measurement_type,
            output_value: m.square_feet ?? m.linear_feet ?? m.metric_value ?? 0,
            output_unit: m.square_feet != null ? "sq ft" : m.linear_feet != null ? "ft" : "m²",
            calculator_key: null,
            input_unit: m.display_units,
            provider: m.provider,
          };
          setMeasurements((xs) => [compat, ...xs]);
          setShowWorkspace(false);
        }}
      />
      {!selectedProp && <div className="text-xs text-zinc-500">Pick or create a property first.</div>}
      {selectedProp && measurements.length === 0 && (
        <div className="text-xs text-zinc-500">
          No measurements yet. Add typed measurements (e.g. lawn 4,000 sqft) — they'll be saved for reuse.
        </div>
      )}
      <div className="space-y-2 mt-2">
        {measurements.map((m) => (
          <div
            key={m.id}
            className="border border-zinc-200 rounded-lg p-2 flex items-center gap-2"
            data-testid={`measurement-row-${m.id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{m.label || m.calculator_key}</div>
              <div className="text-xs text-zinc-500 font-mono-accent">
                {m.output_value.toLocaleString()} {m.output_unit || m.input_unit || ""}
              </div>
            </div>
            <button
              onClick={() => onAddLine(m)}
              className="text-xs text-[#FF5A00] hover:underline"
              data-testid={`add-measurement-to-lines-${m.id}`}
            >
              + Line
            </button>
            <button
              onClick={async () => {
                if (!window.confirm("Delete this measurement?")) return;
                try {
                  await api.delete(`/estimator/measurements/${m.id}`);
                  setMeasurements((xs) => xs.filter((x) => x.id !== m.id));
                } catch { toast.error("Failed"); }
              }}
              className="text-zinc-400 hover:text-red-600"
              data-testid={`delete-measurement-${m.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
